import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {Alert, IconButton, Link, useTheme} from '@mui/material';
import {
  getSentinelHubLinkAlertType,
  hasSentinelHubUser,
  setLinkState,
} from '@account/utils/sentinel-hub';
import {trackEvent} from '@account/analytics';
import {useAuth} from '@apps-common/auth-react';
import {useSentinelHubLinkState} from '@account/hooks/sentinel-hub/useSentinelHubLinkState';
export const UNLINK_FAILED_ALERT_TYPE = 'unlink_failed';
export const UPGRADED_ALERT_TYPE = 'upgraded';
export const NOT_UPGRADED_ALERT_TYPE = 'not_upgraded';
export const NOT_UPGRADED_NOT_LINKED_ALERT_TYPE = 'not_upgraded_not_linked';
export const UPGRADE_FAILED_ALERT_TYPE = 'upgrade_failed';
export const LINK_DUPLICATE_USER_FAILED_ALERT_TYPE =
  'link_duplicate_user_failed';

const SEVERITY_SUCCESS = 'success';
const SEVERITY_ERROR = 'error';
const SEVERITY_INFO = 'info';

export const UNLINK_FAILED_MESSAGE =
  'Unlinking Planet and Sentinel Hub accounts failed. Please try again.';

export const UPGRADED_MESSAGE = (
  <>
    Success! You have upgraded your access to Sentinel Hub. Learn more on our{' '}
    <Link
      style={{color: 'inherit'}}
      href="https://support.planet.com/hc/en-us/sections/16417841573789-Getting-Started"
    >
      Support Center
    </Link>{' '}
    and{' '}
    <Link
      style={{color: 'inherit'}}
      href="https://university.planet.com/page/sentinel-hub-landing-page"
    >
      Planet University
    </Link>
  </>
);

export const NOT_UPGRADED_MESSAGE = (
  <>
    Your user has been linked, but you are not eligible for the Sentinel Hub
    Trial for Planet Customers. Please visit the{' '}
    <Link
      style={{color: 'inherit'}}
      href="https://support.planet.com/hc/en-us/sections/16417841573789-Getting-Started"
    >
      Support Center
    </Link>{' '}
    to learn more about eligibility.
  </>
);

export const NOT_UPGRADED_NOT_LINKED_MESSAGE = (
  <>
    You are not eligible for the Sentinel Hub Trial for Planet Customers. Please
    visit the{' '}
    <Link
      style={{color: 'inherit'}}
      href="https://support.planet.com/hc/en-us/sections/16417841573789-Getting-Started"
    >
      Support Center
    </Link>{' '}
    to learn more about eligibility. If you have any inquiries, please reach out
    to support@planet.com.
  </>
);

export const UPGRADE_FAILED_MESSAGE =
  'Upgrading Sentinel Hub access failed. Please try again.';
export const LINK_DUPLICATE_USER_FAILED_MESSAGE =
  'Sentinel Hub user has already been linked to another Planet user. Please try a different Sentinel Hub user.';

const getAlertStyle = (theme, alertType) => {
  const SUCCESS_ICON_COLOR = theme.palette.success.dark;
  const ERROR_ICON_COLOR = theme.palette.error.dark;

  const styles = {
    [UNLINK_FAILED_ALERT_TYPE]: {
      severity: SEVERITY_ERROR,
      iconColor: ERROR_ICON_COLOR,
      message: UNLINK_FAILED_MESSAGE,
    },
    [UPGRADED_ALERT_TYPE]: {
      severity: SEVERITY_SUCCESS,
      iconColor: SUCCESS_ICON_COLOR,
      message: UPGRADED_MESSAGE,
    },
    [NOT_UPGRADED_ALERT_TYPE]: {
      severity: SEVERITY_INFO,
      message: NOT_UPGRADED_MESSAGE,
    },
    [NOT_UPGRADED_NOT_LINKED_ALERT_TYPE]: {
      severity: SEVERITY_INFO,
      message: NOT_UPGRADED_NOT_LINKED_MESSAGE,
    },
    [UPGRADE_FAILED_ALERT_TYPE]: {
      severity: SEVERITY_ERROR,
      iconColor: ERROR_ICON_COLOR,
      message: UPGRADE_FAILED_MESSAGE,
    },
    [LINK_DUPLICATE_USER_FAILED_ALERT_TYPE]: {
      severity: SEVERITY_ERROR,
      iconColor: ERROR_ICON_COLOR,
      message: LINK_DUPLICATE_USER_FAILED_MESSAGE,
    },
  };

  return styles[alertType];
};

const eventMapping = {
  [UNLINK_FAILED_ALERT_TYPE]:
    'Sentinel Hub: User failed to unlink from Sentinel Hub',
  [UPGRADED_ALERT_TYPE]: {
    true: 'Sentinel Hub: Existing linked user upgraded to trial',
    false: 'Sentinel Hub: User link and upgrade to Planet trial completed',
  },
  [NOT_UPGRADED_ALERT_TYPE]: 'Sentinel Hub: User link completed',
  [NOT_UPGRADED_NOT_LINKED_ALERT_TYPE]:
    'Sentinel Hub: User link failed to link to Sentinel Hub due to disabled org',
  [UPGRADE_FAILED_ALERT_TYPE]: {
    true: 'Sentinel Hub: Existing linked user upgrade to trial failed',
    false: 'Sentinel Hub: User link and upgrade to Planet trial fails',
  },
  [LINK_DUPLICATE_USER_FAILED_ALERT_TYPE]:
    'Sentinel Hub: User failed to link to Sentinel Hub due to duplicate user',
};

const SentinelHubLinkAlert = ({originMetadata = {}}) => {
  const [closed, setClosed] = useState(null);
  const theme = useTheme();
  const auth = useAuth();
  const claims = auth.user?.claims;
  const plUserId = claims?.user_id;
  const {linkState} = useSentinelHubLinkState(plUserId);
  const shUserId = claims?.sh_user_id;

  const params = Object.fromEntries(
    new URLSearchParams(window.location.search)
  );

  const alertType = getSentinelHubLinkAlertType(linkState, params);

  useEffect(() => {
    if (
      alertType &&
      (!linkState?.alertType || linkState?.alertType !== alertType)
    ) {
      // in this block, we've just upgraded, not upgraded, or failed to upgrade the user for the first time
      // emit event using alertType and shuserId to determine the right event before setting link state

      // get name of event to emit
      const checkExistingUser =
        alertType === UPGRADED_ALERT_TYPE ||
        alertType === UPGRADE_FAILED_ALERT_TYPE;
      const isLinked = hasSentinelHubUser(claims);
      const eventName = checkExistingUser
        ? eventMapping[alertType][isLinked]
        : eventMapping[alertType];

      trackEvent(eventName, {
        origin: originMetadata,
        sh_user_id: shUserId,
      });

      setLinkState(plUserId, {...linkState, alertType});
    }
  }, [claims, alertType, shUserId, linkState, originMetadata, plUserId]);

  const alertStyle = getAlertStyle(theme, alertType);

  if (!alertStyle || closed || linkState?.closedLinkAlert) return null;

  return (
    <Alert
      severity={alertStyle?.severity}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 2,
        // action is padded 4px from top of enclosing div by default...
        '.MuiAlert-action': {
          padding: 0,
        },
      }}
      icon={false}
      action={
        <IconButton
          aria-label="Close"
          onClick={() => {
            setClosed(true);
            setLinkState(plUserId, {
              ...linkState,
              closedLinkAlert: true,
            });
          }}
        >
          <CloseIcon fontSize="inherit" sx={{color: alertStyle?.iconColor}} />
        </IconButton>
      }
    >
      {alertStyle?.message}
    </Alert>
  );
};

SentinelHubLinkAlert.propTypes = {
  originMetadata: PropTypes.string,
};

export default SentinelHubLinkAlert;
